import { LeaderGroupCombobox, StickyFilters, UserCombobox } from '@/components'
import {
  Button as BaseButton,
  ExportButton as BaseExportButton,
  Checkbox,
  MonthPicker,
} from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { PlainLink } from '@/components_deprecated/Link'
import type { LeaderGroupNode } from '@/modules/companies'
import { Field } from '@/modules/companies'
import { useAdmin, usePermissions, useUser } from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { Icon } from '@ur-solutions/react-components'
import type { UpdateFormFn } from '@ur-solutions/react-hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useMemo } from 'react'
import { isMobile, isMobileOnly } from 'react-device-detect'
import styled, { css } from 'styled-components'
import ToggleSwitch from '../../../components_deprecated/Toggle/ToggleSwitch'
import type { TimesheetFilterForm } from '../types'

interface LeaderGroupProps {
  $leaderGroupField?: boolean
}

const Wrapper = styled(Card)<LeaderGroupProps>`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  /* The card's own radius and shadow outrank the layered Tailwind classes
     StickyFilters uses for its stuck state, so repeat them here. */
  [data-stuck] > & {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    box-shadow: 0 12px 24px -12px #00305740;
  }

  section {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;

    & + section {
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props =>
    props.$leaderGroupField &&
    css`
      ${props.theme.media.custom({ max: '1750px' })} {
        grid-template-columns: 1fr;

        section {
          display: flex;
          flex-wrap: wrap;

          & + section {
            display: flex;
            justify-content: flex-end;
            padding-top: 0;
            border-left: 0;
          }
        }
      }
    `}
  ${props => props.theme.media.largePad} {
    grid-template-columns: 1fr;

    section {
      display: flex;
      flex-wrap: wrap;

      & + section {
        display: flex;
        justify-content: flex-end;

        padding-top: 0;
        border-left: 0;
      }
    }
  }

  ${props => props.theme.media.custom({ max: '1050px' })} {
    section + section {
      justify-content: flex-start;
    }
  }

  ${props => props.theme.media.mobile} {
    display: flex;
    flex-direction: column;

    section:first-of-type {
      display: flex;
      flex-direction: column;
      gap: 0;

      & + section {
        border-left: 0;
      }

      & > * + * {
        margin-top: 1rem;
      }
    }

    section:last-of-type {
      display: flex;
      justify-content: flex-end;
      padding-top: 0;
    }
  }
`
const Button = styled(BaseButton)<LeaderGroupProps>`
  ${props => props.theme.media.mobile} {
    padding: 1rem !important;
    text-align: center;
  }

  ${props => props.$leaderGroupField && props.theme.media.mobileAndLargePad} {
    padding: 1rem 0.4rem 1rem 2rem !important;

    span {
      display: none;
    }
  }

  ${props => props.$leaderGroupField && props.theme.media.mediumScreen} {
    padding: 1rem 0.4rem 1rem 2rem !important;
  }
`
const ExportButton = styled(BaseExportButton)`
  ${props => props.theme.media.mobile} {
    padding: 1rem !important;
    text-align: center;
  }

  ${props => props.theme.media.mobileAndLargePad} {
    padding: 1rem 0.4rem 1rem 2rem !important;

    span {
      display: none;
    }
  }
`

const ContainerRadioButton = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.theme.colors.gray5};
  }
`

interface TimesheetControlsProps {
  filter: TimesheetFilterForm

  leaderGroupsInfo?: {
    groupLoading: boolean
    leaderGroupFilter?: boolean
    leaderGroups?: LeaderGroupNode[]
  }

  onOpenExportModal?: () => void
  onUpdate: UpdateFormFn<TimesheetFilterForm>
}

export const TimesheetControls: React.FC<TimesheetControlsProps> = ({
  filter,
  leaderGroupsInfo,

  onOpenExportModal = () => void 0,
  onUpdate,
}) => {
  const translations = useTranslate({
    salaryMode: 'common.salary-mode',
    absenceType: 'timesheets.absence-type',
    month: 'common.month',
    user: 'common.user',
    export: 'common.export',
    registerHours: 'timesheets.register-timeentry',
    leaderGroups: 'companies.leader-groups',
    chooseGroup: 'common.choose-group',
  })

  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()

  const canAddTimeEntry = hasPermissions(
    PERMISSIONS.timesheets.add.timeentry,
    true
  )
  const canViewSalaryMode = hasPermissions(
    PERMISSIONS.salary.view.salaryMode,
    false
  )
  const canAdd = canAddTimeEntry
  const showRightSection = admin || canAdd

  const canSeeOthersTimeEntries = hasPermissions(
    PERMISSIONS.timesheets.view.otherstimeentry,
    true
  )

  const canSeeOthers = canSeeOthersTimeEntries
  const selectedLeaderGroup = filter.leaderGroup
  const selectedGroup = leaderGroupsInfo?.leaderGroups?.find(
    group => group.id === selectedLeaderGroup
  )
  const selectedLeaderGroupUsers = useMemo(
    () => new Set(selectedGroup?.users.map(user => user.id) ?? []),
    [selectedGroup]
  )

  const onLeaderGroupChange = (value: string) => {
    const normalizedValue = value || null
    const group = leaderGroupsInfo?.leaderGroups?.find(
      group => group.id === normalizedValue
    )
    const leaderGroupUsers = group?.users.map(user => user.id) ?? []
    const shouldReplaceUser =
      !!normalizedValue &&
      !!filter.user &&
      !leaderGroupUsers.includes(filter.user)
    const nextUser = shouldReplaceUser
      ? leaderGroupUsers[0] ?? me.id
      : filter.user || me.id

    onUpdate({
      leaderGroup: normalizedValue,
      user: nextUser,
    })
  }

  const field = leaderGroupsInfo?.leaderGroupFilter

  return (
    <StickyFilters>
      <Wrapper $leaderGroupField={field}>
        <section>
          {canSeeOthers && !filter.allUsers ? (
            <Field>
              <label>{translations.user}</label>
              <UserCombobox
                mode="single"
                value={filter.user ?? ''}
                clearable={false}
                className={isMobileOnly ? 'w-full' : undefined}
                hideItem={item =>
                  !!selectedLeaderGroup &&
                  !selectedLeaderGroupUsers.has(item.id)
                }
                onChange={user => onUpdate({ user: user || me.id })}
              />
            </Field>
          ) : (
            <></>
          )}

          {leaderGroupsInfo?.leaderGroupFilter && (
            <Field>
              <label>{translations.leaderGroups}</label>
              <LeaderGroupCombobox
                mode="single"
                value={selectedLeaderGroup ?? ''}
                module="Timesheets"
                clearable
                placeholder={translations.chooseGroup}
                onChange={onLeaderGroupChange}
              />
            </Field>
          )}
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={filter.month}
              disableAfter={true}
              minWidth={isMobileOnly ? '0' : '340px'}
              width={isMobileOnly ? '100%' : undefined}
              maxWidth={isMobileOnly ? '100%' : '420px'}
              onChange={nextMonth => onUpdate({ month: nextMonth })}
            />
          </Field>

          {canViewSalaryMode && (
            <Field>
              {isMobile ? (
                <ContainerRadioButton>
                  <label>{translations.salaryMode}</label>
                  <ToggleSwitch
                    value={filter.salaryMode}
                    onChange={checked => onUpdate({ salaryMode: checked })}
                  />
                </ContainerRadioButton>
              ) : (
                <>
                  <label>{translations.salaryMode}</label>
                  <Checkbox
                    checked={filter.salaryMode}
                    onChange={checked => onUpdate({ salaryMode: checked })}
                    boxSize="3.875rem"
                    padding="2rem"
                    boxBorderRadius="8px"
                  />
                </>
              )}
            </Field>
          )}
        </section>

        {showRightSection && (
          <section>
            {admin && (
              <ExportButton
                iconLeftProps={{ icon: 'download' }}
                fullWidth={isMobileOnly}
                onClick={onOpenExportModal}
              >
                {/* TODO not correct icon */}
                {isMobileOnly ? (
                  translations.export
                ) : (
                  <span>{translations.export}</span>
                )}
              </ExportButton>
            )}

            {canAdd && (
              <PlainLink
                to="/time-entries/register"
                state={{ initialUser: filter.user }}
              >
                {isMobileOnly ? (
                  <Button padding="0 1rem !important" $leaderGroupField={field}>
                    <Icon icon="plus" margin="0" />
                  </Button>
                ) : (
                  <Button
                    iconLeftProps={{ icon: 'plus' }}
                    $leaderGroupField={field}
                  >
                    <span>{translations.registerHours}</span>
                  </Button>
                )}
              </PlainLink>
            )}
          </section>
        )}
      </Wrapper>
    </StickyFilters>
  )
}
